import { uniq } from 'lodash-es';
import { PlusIcon, SaveIcon } from 'lucide-react';
import { FC, JSX, useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { StylesConfig } from '@player/content';
import Input from '@semcore/ui/input';

import { Button, Tooltip } from '@/shared';
import AppInput from '@/shared/ui/Input';
import { Select } from '@/shared/ui/select';

const BUILT_IN_FONTS = ['Arial', 'Times New Roman', 'Courier New', 'Helvetica'];

interface ITextSettingsProps {}

const TextSettings: FC<ITextSettingsProps> = (): JSX.Element => {
  const [isInputMode, setIsInputMode] = useState(false);
  const [customFont, setCustomFont] = useState('');

  const {
    control,
    register,
    subscribe,
    setValue,
    formState: { errors },
  } = useFormContext<{ config: StylesConfig }>();

  useEffect(() => {
    return subscribe({
      name: ['config.text.font'],
      callback(data) {
        const { font } = data.values.config.text;

        // We don't want the custom option to disappear
        // when the user selects one of the built-in options.
        if (!BUILT_IN_FONTS.includes(font)) setCustomFont(font);
      },
    });
  }, [subscribe]);

  const options = uniq(BUILT_IN_FONTS.concat(customFont))
    .filter(Boolean)
    .map((font) => ({
      value: font,
      label: font,
    }));

  const saveCustomFont = useCallback(() => {
    setIsInputMode(false);

    if (!customFont) setValue('config.text.font', BUILT_IN_FONTS[0]);
  }, [customFont, setValue]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex">
        <div className="flex grow text-sm font-semibold">Text settings</div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <Controller
            control={control}
            name="config.text.font"
            render={({ field: { value, onChange } }) =>
              isInputMode ? (
                <Input>
                  <Input.Value
                    value={value}
                    onChange={onChange}
                    onBlur={saveCustomFont}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') saveCustomFont();
                    }}
                    autoFocus
                    placeholder="Custom font"
                  />
                </Input>
              ) : (
                <Select
                  multiplyLabel="Font"
                  options={options}
                  current={options.find((option) => option.value === value)}
                  onChange={(selected) =>
                    onChange(
                      Array.isArray(selected)
                        ? selected[0].value
                        : selected.value
                    )
                  }
                />
              )
            }
          />
          {isInputMode ? (
            <Tooltip title="Save">
              <Button
                size="sm"
                variant="secondary"
                iconBefore={<SaveIcon />}
                onClick={saveCustomFont}
                className="p-1.5"
              />
            </Tooltip>
          ) : (
            <Tooltip title="Add custom">
              <Button
                size="sm"
                variant="secondary"
                iconBefore={<PlusIcon />}
                onClick={() => setIsInputMode(true)}
                className="p-1.5"
              />
            </Tooltip>
          )}
        </div>

        <div className="flex gap-3">
          <div className="flex w-1/2">
            <AppInput
              type="color"
              placeholder="Text color"
              className=""
              error={errors?.config?.text?.color?.message ?? ''}
              errorClassName={
                errors.config?.text?.color?.message
                  ? 'text-sm font-light'
                  : 'hidden'
              }
              {...register('config.text.color')}
            />
          </div>
          <div className="flex w-1/2">
            <AppInput
              placeholder="Text size"
              type="number"
              error={errors?.config?.text?.font_size?.message ?? ''}
              errorClassName={
                errors.config?.text?.font_size?.message
                  ? 'text-sm font-light'
                  : 'hidden'
              }
              {...register('config.text.font_size')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextSettings;
